import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BarChart3, TrendingUp, Users, Package, Gavel, Handshake,
  Calendar, ArrowUp, ArrowDown, Minus, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30'); // days

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/analytics?days=${timeRange}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const getChangeIndicator = (change) => {
    if (change > 0) return { icon: ArrowUp, color: 'text-emerald-500', bg: 'bg-emerald-50' };
    if (change < 0) return { icon: ArrowDown, color: 'text-red-500', bg: 'bg-red-50' };
    return { icon: Minus, color: 'text-slate-400', bg: 'bg-slate-50' };
  };

  const timeRanges = [
    { value: '7', label: '7 Days' },
    { value: '30', label: '30 Days' },
    { value: '90', label: '90 Days' },
    { value: '365', label: '1 Year' },
  ];

  if (loading && !analytics) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Analytics & Reports
          </h1>
          <p className="text-slate-400">Platform performance metrics and trends</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-slate-700 rounded-xl p-1">
            {timeRanges.map(range => (
              <button
                key={range.value}
                onClick={() => setTimeRange(range.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  timeRange === range.value
                    ? 'bg-indigo-600 text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>
          <Button
            onClick={fetchAnalytics}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {analytics && (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {/* New Users */}
            <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-indigo-400" />
                </div>
                {analytics.users?.change !== undefined && (
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-lg ${getChangeIndicator(analytics.users.change).bg}`}>
                    {React.createElement(getChangeIndicator(analytics.users.change).icon, {
                      className: `w-4 h-4 ${getChangeIndicator(analytics.users.change).color}`
                    })}
                    <span className={`text-sm font-medium ${getChangeIndicator(analytics.users.change).color}`}>
                      {Math.abs(analytics.users.change)}%
                    </span>
                  </div>
                )}
              </div>
              <p className="text-3xl font-bold text-white mb-1">{formatNumber(analytics.users?.new || 0)}</p>
              <p className="text-sm text-slate-400">New Users</p>
              <p className="text-xs text-slate-500 mt-2">Total: {formatNumber(analytics.users?.total || 0)}</p>
            </div>

            {/* New Listings */}
            <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                  <Package className="w-6 h-6 text-emerald-400" />
                </div>
                {analytics.listings?.change !== undefined && (
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-lg ${getChangeIndicator(analytics.listings.change).bg}`}>
                    {React.createElement(getChangeIndicator(analytics.listings.change).icon, {
                      className: `w-4 h-4 ${getChangeIndicator(analytics.listings.change).color}`
                    })}
                    <span className={`text-sm font-medium ${getChangeIndicator(analytics.listings.change).color}`}>
                      {Math.abs(analytics.listings.change)}%
                    </span>
                  </div>
                )}
              </div>
              <p className="text-3xl font-bold text-white mb-1">{formatNumber(analytics.listings?.new || 0)}</p>
              <p className="text-sm text-slate-400">New Listings</p>
              <p className="text-xs text-slate-500 mt-2">Total: {formatNumber(analytics.listings?.total || 0)}</p>
            </div>

            {/* New Bids */}
            <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                  <Gavel className="w-6 h-6 text-amber-400" />
                </div>
                {analytics.bids?.change !== undefined && (
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-lg ${getChangeIndicator(analytics.bids.change).bg}`}>
                    {React.createElement(getChangeIndicator(analytics.bids.change).icon, {
                      className: `w-4 h-4 ${getChangeIndicator(analytics.bids.change).color}`
                    })}
                    <span className={`text-sm font-medium ${getChangeIndicator(analytics.bids.change).color}`}>
                      {Math.abs(analytics.bids.change)}%
                    </span>
                  </div>
                )}
              </div>
              <p className="text-3xl font-bold text-white mb-1">{formatNumber(analytics.bids?.new || 0)}</p>
              <p className="text-sm text-slate-400">New Bids</p>
              <p className="text-xs text-slate-500 mt-2">Total: {formatNumber(analytics.bids?.total || 0)}</p>
            </div>

            {/* Completed Trades */}
            <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-violet-500/20 rounded-xl flex items-center justify-center">
                  <Handshake className="w-6 h-6 text-violet-400" />
                </div>
                {analytics.trades?.change !== undefined && (
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-lg ${getChangeIndicator(analytics.trades.change).bg}`}>
                    {React.createElement(getChangeIndicator(analytics.trades.change).icon, {
                      className: `w-4 h-4 ${getChangeIndicator(analytics.trades.change).color}`
                    })}
                    <span className={`text-sm font-medium ${getChangeIndicator(analytics.trades.change).color}`}>
                      {Math.abs(analytics.trades.change)}%
                    </span>
                  </div>
                )}
              </div>
              <p className="text-3xl font-bold text-white mb-1">{formatNumber(analytics.trades?.completed || 0)}</p>
              <p className="text-sm text-slate-400">Completed Trades</p>
              <p className="text-xs text-slate-500 mt-2">Total: {formatNumber(analytics.trades?.total || 0)}</p>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Activity Timeline */}
            <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-indigo-400" />
                Activity Timeline
              </h3>
              <div className="space-y-4">
                {analytics.timeline?.map((day, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <span className="text-xs text-slate-500 w-16">{day.date}</span>
                    <div className="flex-1 flex items-center gap-2">
                      <div className="flex-1 bg-slate-700 rounded-full h-2 overflow-hidden">
                        <div 
                          className="bg-indigo-500 h-full rounded-full transition-all"
                          style={{ width: `${Math.min((day.users / (analytics.timeline[0]?.users || 1)) * 100, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-400 w-8">{day.users}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-700">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-indigo-500" />
                  <span className="text-xs text-slate-400">New Users</span>
                </div>
              </div>
            </div>

            {/* Category Distribution */}
            <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-emerald-400" />
                Category Distribution
              </h3>
              <div className="space-y-3">
                {analytics.categories?.map((cat, idx) => {
                  const colors = ['bg-indigo-500', 'bg-emerald-500', 'bg-amber-500', 'bg-violet-500', 'bg-pink-500', 'bg-cyan-500'];
                  const maxCount = Math.max(...analytics.categories.map(c => c.count));
                  return (
                    <div key={idx} className="flex items-center gap-4">
                      <span className="text-sm text-slate-300 w-28 truncate">{cat.name}</span>
                      <div className="flex-1 bg-slate-700 rounded-full h-3 overflow-hidden">
                        <div 
                          className={`${colors[idx % colors.length]} h-full rounded-full transition-all`}
                          style={{ width: `${(cat.count / maxCount) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm text-slate-400 w-10 text-right">{cat.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Conversion Metrics */}
          <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-amber-400" />
              Conversion Metrics
            </h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-3 relative">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle cx="40" cy="40" r="36" fill="none" stroke="#334155" strokeWidth="8" />
                    <circle 
                      cx="40" cy="40" r="36" fill="none" stroke="#10b981" strokeWidth="8"
                      strokeDasharray={`${(analytics.conversions?.bidRate || 0) * 2.26} 226`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg font-bold text-white">{analytics.conversions?.bidRate || 0}%</span>
                  </div>
                </div>
                <p className="text-sm text-slate-400">Bid Rate</p>
                <p className="text-xs text-slate-500">Listings with bids</p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-3 relative">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle cx="40" cy="40" r="36" fill="none" stroke="#334155" strokeWidth="8" />
                    <circle 
                      cx="40" cy="40" r="36" fill="none" stroke="#8b5cf6" strokeWidth="8"
                      strokeDasharray={`${(analytics.conversions?.acceptRate || 0) * 2.26} 226`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg font-bold text-white">{analytics.conversions?.acceptRate || 0}%</span>
                  </div>
                </div>
                <p className="text-sm text-slate-400">Accept Rate</p>
                <p className="text-xs text-slate-500">Bids accepted</p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-3 relative">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle cx="40" cy="40" r="36" fill="none" stroke="#334155" strokeWidth="8" />
                    <circle 
                      cx="40" cy="40" r="36" fill="none" stroke="#f59e0b" strokeWidth="8"
                      strokeDasharray={`${(analytics.conversions?.tradeRate || 0) * 2.26} 226`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg font-bold text-white">{analytics.conversions?.tradeRate || 0}%</span>
                  </div>
                </div>
                <p className="text-sm text-slate-400">Trade Rate</p>
                <p className="text-xs text-slate-500">Trades completed</p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-3 relative">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle cx="40" cy="40" r="36" fill="none" stroke="#334155" strokeWidth="8" />
                    <circle 
                      cx="40" cy="40" r="36" fill="none" stroke="#06b6d4" strokeWidth="8"
                      strokeDasharray={`${(analytics.conversions?.returnRate || 0) * 2.26} 226`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg font-bold text-white">{analytics.conversions?.returnRate || 0}%</span>
                  </div>
                </div>
                <p className="text-sm text-slate-400">Return Users</p>
                <p className="text-xs text-slate-500">Multiple trades</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
